
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import Link from 'next/link'
import { Icon } from '@/components/ui/Icon'
import { formatDate } from '@/lib/format'
import { formatVa } from '@/lib/power'
import type { PowerPlanSummary } from '@/lib/power-plan-server'
import { btn, card, cx, notice, pageIntro, pageTitle, tag, tagTone } from '@/lib/ui'
import { errorMessage } from '@/lib/client-api'

type Project = { id: string; name: string; nodeCount: number }

/**
 * The power index. Each card carries the answer rather than the filename: a
 * crew opens this page to find out how big the supply has to be, so "11.4 kVA"
 * is on the card and the name is the caption.
 */
export function PowerIndexClient({
  initialPlans,
  projects,
  seedProjectId,
  apiBase,
  basePath,
}: {
  initialPlans: PowerPlanSummary[]
  projects: Project[]
  /** Set when the crew came here from a canvas that has no plan yet. */
  seedProjectId: string | null
  apiBase: string
  basePath: string
}) {
  const router = useRouter()
  const [plans] = useState(initialPlans)
  const [creating, setCreating] = useState(false)
  const seedFrom = seedProjectId ? (projects.find((p) => p.id === seedProjectId) ?? null) : null
  const [error, setError] = useState<string | null>(null)

  async function create(seedFromProjectId?: string) {
    setCreating(true)
    setError(null)
    try {
      const seed = seedFromProjectId ? projects.find((p) => p.id === seedFromProjectId) : null
      const response = await fetch(`${apiBase}/power`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: seed ? `${seed.name} — power` : 'Untitled power plan',
          ...(seedFromProjectId ? { seedFromProjectId } : {}),
        }),
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Could not create the plan')
      }
      const plan = (await response.json()) as { id: string }
      router.push(`${basePath}/power/${plan.id}`)
    } catch (cause) {
      setError(errorMessage(cause, 'Could not create the plan'))
      setCreating(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle}>Power</h1>
          <p className={pageIntro}>
            What the show draws, and whether the supply covers it. A load schedule for the venue or
            the generator hire — not an electrical design.
          </p>
        </div>
        <button
          type="button"
          onClick={() => create()}
          disabled={creating}
          className={cx(btn.base, btn.primary)}
        >
          <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
          New power plan
        </button>
      </div>

      {error && (
        <p role="alert" className={cx(notice.error, 'mt-4')}>
          {error}
        </p>
      )}

      {seedFrom && (
        <div className={cx(card, 'mt-5 flex flex-wrap items-center justify-between gap-3 px-5 py-4')}>
          <p className="text-sm">
            <span className="font-medium">{seedFrom.name}</span> has no power plan yet.
            <span className="text-muted">
              {' '}
              Seeding one brings across all {seedFrom.nodeCount} placed{' '}
              {seedFrom.nodeCount === 1 ? 'device' : 'devices'} with their catalogue figures.
            </span>
          </p>
          <button
            type="button"
            onClick={() => create(seedFrom.id)}
            disabled={creating}
            className={cx(btn.base, btn.primary)}
          >
            Create from this canvas
          </button>
        </div>
      )}

      {plans.length === 0 ? (
        <div className={cx(card, 'gradient-hero mt-6 px-6 py-12 text-center')}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface/80 text-foreground/70 shadow-[var(--shadow-card)]">
            <Icon name="plug" className="h-6 w-6" />
          </span>
          <p className="mx-auto mt-4 max-w-md text-sm text-muted">
            No power plans yet. Start from a canvas you have already drawn — the gear on it comes
            across with its catalogue figures.
          </p>
          <div className="mx-auto mt-5 flex max-w-md flex-wrap justify-center gap-2">
            {projects.slice(0, 3).map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => create(project.id)}
                disabled={creating}
                className={cx(btn.sm, btn.secondary)}
              >
                <Icon name="projects" className="h-3.5 w-3.5" />
                {project.name}
                <span className="text-muted">· {project.nodeCount}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => create()}
              disabled={creating}
              className={cx(btn.sm, projects.length === 0 ? btn.primary : btn.ghost)}
            >
              Start blank
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {plans.map((plan) => (
            <Link
              key={plan.id}
              href={`${basePath}/power/${plan.id}`}
              className={cx(card, 'group block px-5 py-4 transition-shadow hover:shadow-[var(--shadow-card-hover)]')}
            >
              <div className="flex items-baseline justify-between gap-3">
                <p className="truncate text-sm font-medium">{plan.name}</p>
                <Icon
                  name="arrowRight"
                  className="h-4 w-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5"
                />
              </div>

              <p className="mt-2 text-xl font-semibold tabular-nums tracking-tight">
                {formatVa(plan.vaRequired)}
                <span className="ml-1.5 text-[11px] font-normal uppercase tracking-[0.08em] text-muted">
                  required
                </span>
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {plan.worstLeg && (
                  <span className={cx(tag, tagTone.neutral)}>
                    worst leg {plan.worstLeg.leg} · {plan.worstLeg.amps.toFixed(1)} A
                  </span>
                )}
                {plan.unknownRows > 0 && (
                  <span className={cx(tag, tagTone.warning)}>
                    {plan.unknownRows} without a figure
                  </span>
                )}
              </div>

              <p className="mt-2.5 text-[11px] text-muted">
                {[
                  plan.projectName,
                  `${plan.itemCount} ${plan.itemCount === 1 ? 'row' : 'rows'}`,
                  plan.groupCount > 0 && `${plan.groupCount} circuits`,
                  formatDate(plan.updatedAt),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </Link>
          ))}
        </div>
      )}

      {plans.length > 0 && projects.length > 0 && (
        <div className={cx(card, 'mt-4 px-5 py-4')}>
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">
            Start from a canvas
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {projects.slice(0, 6).map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => create(project.id)}
                disabled={creating}
                className={cx(btn.sm, btn.secondary)}
              >
                <Icon name="projects" className="h-3.5 w-3.5" />
                {project.name}
                <span className="text-muted">· {project.nodeCount}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
